import { useEffect, useRef, useState } from "react";
import { LuDices, LuLock, LuStar } from "react-icons/lu";
import { TurnFrame, type GameProps } from "../pages/Match";
import { calm } from "../components/fx";
import { banner, pop, useLogEvents, useRecent } from "../components/juice";
import { buzz, play } from "../sound";
import { BASE_ORIGIN, GOAL, GOAL_CELL, HOME_COLUMN, RING, isSafeIndex, pieceXY } from "../../shared/games/ludo";
import { BONUS_LIMIT, BONUS_POINTS, UPPER_COUNT, YATZY_CATS, upperSum } from "../../shared/games/yatzy";

const canAct = (v: any) => v.state.phase === "action" && v.state.turn === v.state.you;

/* ───────────── a die ───────────── */
const PIPS: Record<number, number[]> = { 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8] };
/** v17: a real cube (CSS 3D). Opposite faces add up to 7; the cube is turned so that the rolled value faces the player, and every roll adds full turns so it tumbles the same way each time. */
const CUBE_TO: Record<number, [number, number]> = { 1: [0, 0], 2: [0, -90], 3: [-90, 0], 4: [90, 0], 5: [0, 90], 6: [0, 180] };
export function Die({ value, held, rolling, onClick, disabled, size = "m", tone, label }: { value: number | null; held?: boolean; rolling?: boolean; onClick?: () => void; disabled?: boolean; size?: "s" | "m" | "l"; tone?: string; label?: string }) {
  const turns = useRef(0);
  const was = useRef(false);
  if (rolling && !was.current) turns.current += 1;
  was.current = !!rolling;
  const [bx, by] = CUBE_TO[value ?? 1] ?? [0, 0];
  const pipsOf = (f: number) => (value === null ? (f === 1 ? PIPS[5]! : []) : PIPS[f]!);
  const inner = (
    <>
      <span className="cube" aria-hidden="true">
        {[1, 2, 3, 4, 5, 6].map((f) => (
          <span key={f} className={`face f${f}`}>
            {Array.from({ length: 9 }, (_, i) => <i key={i} className={pipsOf(f).includes(i) ? (value === null ? "ghost" : "on") : ""} />)}
          </span>
        ))}
      </span>
      {held && <span className="die-held" aria-hidden="true"><LuLock size={12} /></span>}
    </>
  );
  const cls = `die ${size} ${held ? "held" : ""} ${rolling ? "rolling" : ""}`;
  const style = { ["--rx" as any]: `${bx - turns.current * 720}deg`, ["--ry" as any]: `${by + turns.current * 360}deg`, ...(tone ? { ["--dt" as any]: tone } : {}) };
  if (!onClick) return <span className={cls} style={style} role="img" aria-label={label ?? (value ? `Terning: ${value}` : "Terning")}>{inner}</span>;
  return <button type="button" className={cls} style={style} disabled={disabled} aria-pressed={!!held} aria-label={`${label ?? `Terning ${value}`}${held ? " – beholdt" : ""}. Tryk for at ${held ? "slippe" : "beholde"} den`} onClick={onClick}>{inner}</button>;
}

/* ───────────── Ludo ───────────── */
const ARM = [
  { name: "Rød", c: "#e5484d", d: "#a8262b", l: "#ffe1e3" },
  { name: "Grøn", c: "#22a05a", d: "#14703c", l: "#d6f5e2" },
  { name: "Gul", c: "#f5b301", d: "#a87400", l: "#fff1c2" },
  { name: "Blå", c: "#3b82f6", d: "#1d4fb8", l: "#dbe8ff" },
];

/** walks a piece one square at a time to its new place (and jumps when it is sent home or leaves the base) */
function useSteps(target: number): number {
  const [shown, setShown] = useState(target);
  const cur = useRef(target);
  useEffect(() => {
    if (target === cur.current) return;
    if (target < cur.current || cur.current < 0 || calm()) { cur.current = target; setShown(target); return; }
    const id = setInterval(() => {
      cur.current++;
      setShown(cur.current);
      if (cur.current < GOAL) play("step");
      if (cur.current >= target) clearInterval(id);
    }, 125);
    return () => clearInterval(id);
  }, [target]);
  return shown;
}

function LudoPiece({ arm, piece, target, dx, dy, legal, onPick, label }: { arm: number; piece: number; target: number; dx: number; dy: number; legal: boolean; onPick: () => void; label: string }) {
  const p = useSteps(target);
  const [x, y] = pieceXY(arm, p, piece);
  const c = ARM[arm]!;
  return (
    <g className={`lpiece ${legal ? "legal" : ""} ${p >= GOAL ? "home" : ""}`} style={{ transform: `translate(${x + dx}px, ${y + dy}px)` }}
      role={legal ? "button" : undefined} tabIndex={legal ? 0 : undefined} aria-label={legal ? `Flyt ${label}` : undefined}
      onClick={legal ? onPick : undefined} onKeyDown={legal ? (e: any) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(); } } : undefined}>
      {legal && <circle className="lp-ring" r={0.66} />}
      <ellipse cy={0.34} rx={0.4} ry={0.15} fill="#000" opacity={0.25} />
      <path d="M-0.38 0.34 Q-0.38 0.14 -0.17 0.07 Q-0.23 0 -0.2 -0.1 H0.2 Q0.23 0 0.17 0.07 Q0.38 0.14 0.38 0.34Z" fill={c.c} stroke="#fff" strokeWidth={0.08} strokeLinejoin="round" />
      <circle cy={-0.27} r={0.25} fill={c.c} stroke="#fff" strokeWidth={0.08} />
      <path d="M-0.38 0.34 Q-0.38 0.14 -0.17 0.07 Q-0.23 0 -0.2 -0.1 H0.2 Q0.23 0 0.17 0.07 Q0.38 0.14 0.38 0.34Z" fill="none" stroke={c.d} strokeWidth={0.04} strokeLinejoin="round" />
      <circle cx={-0.09} cy={-0.34} r={0.07} fill="#fff" opacity={0.65} />
      <circle cy={-0.1} r={0.7} fill="transparent" />
    </g>
  );
}

function LudoBoard({ p, onPick }: { p: GameProps; onPick: (piece: number) => void }) {
  const st = p.view.state;
  const b = st.board;
  const arms: number[] = b.arms;
  const you: number = st.you;
  const players: any[] = p.view.players;
  const nameOf = (seat: number) => players.find((x) => x.seat === seat)?.username ?? "Spiller";
  /* pieces that share a square get a little offset so that all of them can be seen */
  const placed = b.pos.flatMap((ps: number[], seat: number) => ps.map((pos, i) => ({ seat, i, pos, xy: pieceXY(arms[seat]!, pos, i) })));
  const groups = new Map<string, { seat: number; i: number }[]>();
  for (const q of placed) { const k = `${q.xy[0].toFixed(2)},${q.xy[1].toFixed(2)}`; groups.set(k, [...(groups.get(k) ?? []), q]); }
  const offset = (seat: number, i: number, xy: [number, number]) => {
    const g = groups.get(`${xy[0].toFixed(2)},${xy[1].toFixed(2)}`)!;
    if (g.length < 2) return [0, 0] as const;
    const k = g.findIndex((q) => q.seat === seat && q.i === i);
    const a = (k / g.length) * Math.PI * 2;
    return [Math.cos(a) * 0.2, Math.sin(a) * 0.2] as const;
  };
  const legal = new Set<number>(b.legal ?? []);
  const mine = canAct(p.view) && b.stage === "move" && !p.busy;
  return (
    <svg className="ludo" viewBox="-0.35 -0.35 15.7 15.7" role="group" aria-label="Ludo-bræt" focusable="false">
      <rect x={-0.3} y={-0.3} width={15.6} height={15.6} rx={1.1} className="ludo-bg" />
      {/* bases */}
      {ARM.map((c, a) => {
        const [ox, oy] = BASE_ORIGIN[a]!;
        const used = arms.includes(a);
        return (
          <g key={a} opacity={used ? 1 : 0.35}>
            <rect x={ox} y={oy} width={6} height={6} rx={0.7} fill={c.c} />
            <rect x={ox + 0.9} y={oy + 0.9} width={4.2} height={4.2} rx={0.6} fill={c.l} />
            {[[2, 2], [4, 2], [2, 4], [4, 4]].map(([sx, sy], k) => <circle key={k} cx={ox + sx!} cy={oy + sy!} r={0.52} fill="#fff" stroke={c.c} strokeWidth={0.08} />)}
          </g>
        );
      })}
      {/* the ring */}
      {RING.map(([x, y], i) => {
        const startOf = i % 13 === 0 ? i / 13 : -1;
        const star = i % 13 === 8;
        return (
          <g key={i}>
            <rect x={x} y={y} width={1} height={1} className="ludo-cell" fill={startOf >= 0 ? ARM[startOf]!.c : "#fff"} />
            {star && <polygon transform={`translate(${x + 0.5} ${y + 0.52})`} points="0,-0.34 0.1,-0.1 0.34,-0.1 0.15,0.06 0.22,0.32 0,0.17 -0.22,0.32 -0.15,0.06 -0.34,-0.1 -0.1,-0.1" fill="#f0a500" />}
            {startOf >= 0 && <polygon transform={`translate(${x + 0.5} ${y + 0.5})`} points="0,-0.3 0.12,-0.08 0.3,-0.08 0.16,0.07 0.2,0.28 0,0.16 -0.2,0.28 -0.16,0.07 -0.3,-0.08 -0.12,-0.08" fill="#fff" opacity={0.9} />}
          </g>
        );
      })}
      {/* home columns and the middle */}
      {HOME_COLUMN.map((col, a) => col.map(([x, y], k) => <rect key={`${a}-${k}`} x={x} y={y} width={1} height={1} className="ludo-cell" fill={ARM[a]!.c} opacity={arms.includes(a) ? 0.95 : 0.35} />))}
      <polygon points="6,6 6,9 7.5,7.5" fill={ARM[0]!.c} /><polygon points="6,6 9,6 7.5,7.5" fill={ARM[1]!.c} />
      <polygon points="9,6 9,9 7.5,7.5" fill={ARM[2]!.c} /><polygon points="6,9 9,9 7.5,7.5" fill={ARM[3]!.c} />
      <rect x={6} y={6} width={3} height={3} fill="none" stroke="#fff" strokeWidth={0.07} />
      <circle cx={7.5} cy={7.5} r={0.3} fill="#fff" opacity={0.9} />
      {GOAL_CELL.map(([gx, gy], a) => <circle key={a} cx={gx + 0.5} cy={gy + 0.5} r={0.05} fill="#fff" opacity={0} />)}
      {/* pieces */}
      {b.pos.flatMap((ps: number[], seat: number) => ps.map((pos, i) => {
        const arm = arms[seat]!;
        const [dx, dy] = offset(seat, i, pieceXY(arm, pos, i));
        const isLegal = seat === you && mine && legal.has(i);
        return <LudoPiece key={`${seat}-${i}`} arm={arm} piece={i} target={pos} dx={dx} dy={dy} legal={isLegal} onPick={() => onPick(i)} label={`${ARM[arm]!.name.toLowerCase()} brik ${i + 1}${nameOf(seat) ? "" : ""}`} />;
      }))}
    </svg>
  );
}

export function Ludo(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const players: any[] = p.view.players;
  const you: number = st.you;
  const mineMove = canAct(p.view) && b.stage === "move";
  const rollId: number = [...(st.log ?? [])].reverse().find((e: any) => e.kind === "roll")?.id ?? 0;
  const rolling = useRecent(rollId, 700);
  const rollerSeat: number = [...(st.log ?? [])].reverse().find((e: any) => e.kind === "roll")?.seat ?? st.turn;
  useLogEvents(st.log, p.view.id, (e) => {
    if (e.kind === "roll") { play("dice"); buzz(15); }
    else if (e.kind === "capture") {
      play("capture"); buzz([30, 30, 50]);
      if (e.data?.victim === you) banner("Din brik blev slået hjem", "pink"); else pop("Slået hjem!", "gold");
    } else if (e.kind === "home") { play("home"); pop("I mål!", "gold"); buzz([25, 30, 25]); }
    else if (e.kind === "bonus") pop("Ekstra slag!", "blue");
    else if (e.kind === "nomove") pop("Ingen træk", "soft");
  });
  const nameOf = (seat: number) => players.find((x) => x.seat === seat)?.username ?? "Spiller";
  const pick = (piece: number) => { void p.act({ type: "move", piece }); };
  const legal: number[] = b.legal ?? [];
  return (
    <TurnFrame {...p} wide statusExtra={mineMove ? <span> · Du slog <b>{b.dice}</b> – vælg en brik</span> : undefined}>
      <div className="ludo-wrap">
        <div className="board-frame ludo-frame"><LudoBoard p={p} onPick={pick} /></div>
        <div className="ludo-side">
          <div className="ludo-die" aria-live="polite">
            <Die value={b.dice} rolling={rolling} size="l" tone={ARM[b.arms[rollerSeat] ?? 0]?.c} label={b.dice ? `Terningen viser ${b.dice}` : "Terningen"} />
            <small className="muted">{b.dice ? `${nameOf(rollerSeat)} slog ${b.dice}` : "Svar rigtigt for at slå"}</small>
          </div>
          <ul className="ludo-players" aria-label="Spillere">
            {players.map((pl) => (
              <li key={pl.seat} className={`${st.turn === pl.seat && st.phase !== "over" ? "turn" : ""}`} style={{ ["--lc" as any]: ARM[b.arms[pl.seat]]?.c }}>
                <i aria-hidden="true" />
                <span>{pl.username}{pl.seat === you ? " (dig)" : ""}</span>
                <b aria-label={`${b.home[pl.seat]} af ${b.pieces} brikker i mål`}>{b.home[pl.seat]}/{b.pieces}</b>
              </li>
            ))}
          </ul>
          {mineMove && !p.busy && (
            <div className="ludo-pick" role="group" aria-label="Vælg hvilken brik der rykker">
              {legal.map((i) => (
                <button key={i} type="button" className="btn lime" onClick={() => pick(i)} style={{ ["--lc" as any]: ARM[b.arms[you]]?.c }}>
                  <i className="swatch" aria-hidden="true" /> Flyt brik {i + 1}{b.pos[you][i] < 0 ? " ud af basen" : b.pos[you][i] + b.dice >= b.goal ? " i mål" : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <small className="muted center">Seksere henter en brik ud og giver et ekstra slag. Stjernefelter og startfelter er trygge. Du skal ramme målet med det helt rigtige tal.</small>
    </TurnFrame>
  );
}

/* ───────────── Yatzy ───────────── */
export function Yatzy(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const players: any[] = p.view.players;
  const you: number = st.you;
  const myPlay = canAct(p.view) && b.stage === "play" && !p.busy;
  const [held, setHeld] = useState<boolean[]>([false, false, false, false, false]);
  const lastHeld = useRef<boolean[] | null>(null);
  useEffect(() => { setHeld([false, false, false, false, false]); }, [b.rollNo, b.turn]);
  const justRolled = useRecent(b.rollNo, 650);
  const catLabel = (i: number) => YATZY_CATS[i]!.label;
  const nameOf = (seat: number) => players.find((x) => x.seat === seat)?.username ?? "Spiller";
  useLogEvents(st.log, p.view.id, (e) => {
    if (e.kind === "roll") { play("dice"); buzz(15); }
    else if (e.kind === "score") { play("place"); if (e.seat !== you) pop(`${nameOf(e.seat!)}: ${e.data?.points} i ${catLabel(e.data?.cat).toLowerCase()}`, "soft"); else pop(`+${e.data?.points}`, "good"); }
    else if (e.kind === "yatzy") { play("win"); banner("YATZY! +50", "gold"); buzz([30, 40, 30, 40, 80]); }
    else if (e.kind === "bonus") { play("streak"); banner(`Bonus! +${BONUS_POINTS}`, "gold"); }
  });
  const canRoll = myPlay && b.throwsLeft > 0 && !held.every(Boolean);
  const toggle = (i: number) => { if (myPlay && b.throwsLeft > 0) setHeld((h) => h.map((x, k) => (k === i ? !x : x))); };
  const roll = () => { lastHeld.current = held.slice(); void p.act({ type: "roll", held }); };
  const upperRows = YATZY_CATS.slice(0, UPPER_COUNT);
  const lowerRows = YATZY_CATS.slice(UPPER_COUNT);
  const cell = (seat: number, ci: number) => {
    const v: number | null = b.sheets[seat][ci];
    if (v !== null) return <td key={seat} className={`y-v ${b.last && b.last.seat === seat && b.last.cat === ci ? "new" : ""}`}>{v}</td>;
    const pot: number | null = seat === you ? b.potential[ci] : null;
    if (seat === you && myPlay && pot !== null) {
      return <td key={seat} className="y-v pick"><button type="button" className={`ypick ${pot > 0 ? "pos" : "zero"}`} onClick={() => p.act({ type: "score", cat: ci })} aria-label={`${catLabel(ci)}: skriv ${pot} point`}>{pot}</button></td>;
    }
    return <td key={seat} className="y-v empty" />;
  };
  const row = (c: (typeof YATZY_CATS)[number], ci: number) => (
    <tr key={c.id}><th scope="row" title={c.hint}>{c.label}<small>{c.hint}</small></th>{players.map((pl) => cell(pl.seat, ci))}</tr>
  );
  const dice: number[] = b.dice;
  const waitingForQuestion = b.stage === "ask";
  return (
    <TurnFrame {...p} wide statusExtra={myPlay ? <span> · {b.throwsLeft > 0 ? `${b.throwsLeft} slag tilbage` : "Vælg et felt på pointarket"}</span> : undefined}>
      <div className="yatzy">
        <section className="y-table" aria-label="Terninger">
          <div className="y-head">
            <span className="y-turn">{waitingForQuestion ? `${nameOf(b.turn)} skal svare for at slå` : `${nameOf(b.turn)}s terninger`}</span>
            <span className="y-round">Runde {b.round}/{b.rounds}</span>
          </div>
          <div className={`y-dice ${waitingForQuestion ? "idle" : ""}`}>
            {dice.map((d, i) => (
              <Die key={i} value={waitingForQuestion ? null : d} size="l" held={!waitingForQuestion && (myPlay ? held[i] : false)} rolling={justRolled && !waitingForQuestion && (!lastHeld.current || b.turn !== you || !lastHeld.current[i])}
                onClick={myPlay && b.throwsLeft > 0 ? () => toggle(i) : undefined} disabled={!myPlay} label={`Terning ${i + 1}: ${d}`} />
            ))}
          </div>
          {myPlay ? (
            <div className="y-actions">
              <button type="button" className="btn cta" disabled={!canRoll} onClick={roll}><LuDices size={18} aria-hidden="true" /> {b.throwsLeft > 0 ? `Slå igen (${b.throwsLeft} tilbage)` : "Ingen slag tilbage"}</button>
              <small className="muted">{b.throwsLeft > 0 ? "Tryk på de terninger, du vil beholde. Eller vælg et felt til højre med det samme." : "Tryk på et felt i pointarket for at skrive dine point."}</small>
            </div>
          ) : (
            <p className="muted center" style={{ margin: 0 }}>{waitingForQuestion ? (b.turn === you ? "Svar på spørgsmålet – så slår du terningerne." : `${nameOf(b.turn)} tænker…`) : b.turn === you ? "Vent et øjeblik…" : `${nameOf(b.turn)} vælger…`}</p>
          )}
          <p className="y-note"><LuStar size={14} aria-hidden="true" /> Rigtigt svar = alle tre slag. Forkert svar = kun ét slag, men du udfylder stadig et felt.</p>
        </section>
        <section className="y-sheet" aria-label="Pointark">
          <table className="ysheet">
            <thead>
              <tr><th />{players.map((pl) => <th key={pl.seat} scope="col" className={b.turn === pl.seat && st.phase !== "over" ? "turn" : ""}>{pl.seat === you ? "Dig" : pl.username}</th>)}</tr>
            </thead>
            <tbody>
              {upperRows.map((c, i) => row(c, i))}
              <tr className="sub"><th scope="row">Øverst i alt<small>Bonus {BONUS_POINTS} ved {BONUS_LIMIT}</small></th>{players.map((pl) => { const u = upperSum(b.sheets[pl.seat]); return <td key={pl.seat} className={`y-v ${u >= BONUS_LIMIT ? "ok" : ""}`}>{u}<small>/{BONUS_LIMIT}</small></td>; })}</tr>
              <tr className="sub"><th scope="row">Bonus</th>{players.map((pl) => <td key={pl.seat} className={`y-v ${b.bonus[pl.seat] ? "ok" : ""}`}>{b.bonus[pl.seat] ? `+${BONUS_POINTS}` : "–"}</td>)}</tr>
              {lowerRows.map((c, i) => row(c, i + UPPER_COUNT))}
              <tr className="total"><th scope="row">I alt</th>{players.map((pl) => <td key={pl.seat} className="y-v">{b.totals[pl.seat]}</td>)}</tr>
            </tbody>
          </table>
        </section>
      </div>
    </TurnFrame>
  );
}
